<template>
  <div class="memory-module" :class="{ collapsed: isCollapsed }">
    <div class="sci-fi-title" @click="toggleCollapse" :title="isCollapsed ? langMap.dashboard_expandModule[lang] : langMap.dashboard_collapseModule[lang]">
      <div class="title-bg"></div>
      <h3>
        <Icon type="ios-albums" /> {{ langMap.dashboard_memoryAndDisk[lang] }}
        <span v-if="memoryData && memoryData.total || diskData && diskData.totalCapacity" class="memory-overview">
          <span class="overview-label">{{ langMap.dashboard_usageRate[lang] }}:</span>
          <span v-if="memoryData && memoryData.total" :class="['overview-value', getUsageClass(memoryData.usageRate)]">{{ (parseFloat(memoryData.usageRate) * 100).toFixed(1) }}%</span>
          <span v-if="memoryData && memoryData.total && diskData && diskData.totalCapacity" class="separator">/</span>
          <span v-if="diskData && diskData.totalCapacity" :class="['overview-value', getUsageClass(parseFloat(diskData.usageRate))]" :title="diskData.usageRate + '%'">{{ parseFloat(diskData.usageRate).toFixed(2) }}%</span>
        </span>
        <Icon :type="isCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'" class="collapse-icon" />
      </h3>
      <div class="title-decoration"></div>
    </div>

    <div class="memory-grid" v-show="!isCollapsed">
      <!-- 无数据提示 -->
      <div v-if="!memoryData || !memoryData.total" class="no-data-state">
        <div class="hologram-container">
          <div class="hologram-ring ring-1"></div>
          <div class="hologram-ring ring-2"></div>
          <div class="hologram-ring ring-3"></div>
          <div class="hologram-icon">
            <Icon type="ios-albums" />
          </div>
        </div>
        <div class="no-data-text">
          <div class="main-text">{{ langMap.dashboard_noData[lang] }}</div>
          <div class="sub-text">{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </div>
      
      <div v-if="memoryData && memoryData.total || diskData && diskData.totalCapacity" class="columns-container">
        <!-- 左列 - 内存信息 -->
        <div v-if="memoryData && memoryData.total" class="column-left">
          <div class="section-header">
            <Icon type="ios-albums" />
            <span>{{ langMap.dashboard_memoryInfo[lang] }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_memoryTotal[lang] }}</span>
            <span class="info-value">{{ memoryData.total }}<span class="unit">GB</span></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_memoryUsed[lang] }}</span>
            <span class="info-value"><span class="used">{{ memoryData.used }}</span><span class="unit">GB</span></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_memoryUsageRate[lang] }}</span>
            <span class="info-value" :class="getUsageClass(memoryData.usageRate)">{{ (parseFloat(memoryData.usageRate) * 100).toFixed(1) }}<span class="unit">%</span></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_memoryFree[lang] }}</span>
            <span class="info-value"><span class="free">{{ memoryData.free }}</span><span class="unit">GB</span></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_memoryBuffer[lang] }}</span>
            <span class="info-value">{{ memoryData.buffer }}<span class="unit">GB</span></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_memoryCached[lang] }}</span>
            <span class="info-value">{{ memoryData.cached }}<span class="unit">GB</span></span>
          </div>
        </div>
        
        <!-- 右列 - 磁盘信息 -->
        <div v-if="diskData && diskData.totalCapacity" class="column-right">
          <div class="section-header">
            <Icon type="ios-folder" />
            <span>{{ langMap.dashboard_diskInfo[lang] }}</span>
          </div>
          <div class="info-row">
            <span class="info-label" style="width: 46px;">{{ langMap.dashboard_deviceName[lang] }}</span>
            <span class="info-value" style="width: 86px;font-size: 11px;">{{ diskData.deviceName }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_mountPoint[lang] }}</span>
            <span class="info-value">{{ diskData.mountPoint }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_fileSystem[lang] }}</span>
            <span class="info-value">{{ diskData.fsType }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_totalCapacity[lang] }}</span>
            <span class="info-value">{{ diskData.totalCapacity }}<span class="unit">GB</span></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_freeCapacity[lang] }}</span>
            <span class="info-value"><span class="free">{{ diskData.freeCapacity }}</span><span class="unit">GB</span></span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ langMap.dashboard_usageRate[lang] }}</span>
            <span class="info-value" :class="getUsageClass(parseFloat(diskData.usageRate))">{{ parseFloat(diskData.usageRate).toFixed(2) }}<span class="unit">%</span></span>
          </div>
        </div>
      </div>
    </div>
  </div>  
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'
import collapsibleMixin from './collapsible-mixin'

export default {
  name: 'MemoryModule',
  mixins: [collapsibleMixin],
  props: {
    memoryData: {
      type: Object,
      default: () => ({})
    },
    diskData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      langMap,
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language
    })
  },
  methods: {
    getUsageClass(usageRate) {
      const u = parseFloat(usageRate)
      // usageRate 可能是 0-1 之间的小数（内存），或直接是百分比是磁盘）
      // 如果是小于1，乘以1000来判断
      const percent = u < 1 ? u * 100 : u
      if (percent >= 90) return 'high'
      if (percent >= 70) return 'medium'
      return 'normal'
    }
  }
}
</script>

<style lang="less" scoped>
.memory-module {
  /* 与其他模块一致的容器样式 */
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 10, 25, 0.95));
  border-radius: 8px;
  padding: 0 0 8px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  min-height: 0;

  &.collapsed { flex: 0 0 auto; }

  .sci-fi-title {
    padding: 12px 12px;
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    position: relative;
    z-index: 10;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;

    &:hover { background: linear-gradient(135deg, rgba(0, 50, 90, 0.9), rgba(0, 30, 50, 0.95)); border-bottom-color: rgba(0, 240, 255, 0.8); }

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
      letter-spacing: 2px;
      display: flex;
      align-items: center;
      gap: 5px;
      
      .memory-overview {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-left: auto;
        margin-right: 8px;
        font-size: 11px;
        font-weight: 400;
        letter-spacing: 0.5px;
        
        .overview-label {
          color: rgba(255, 255, 255, 0.6);
          font-size: 10px;
          white-space: nowrap;
        }
        
        .overview-value {
          font-family: 'Consolas', monospace;
          font-weight: 700;
          font-size: 12px;
          color: #fff;
          
          &.high { color: #ff4d4f; }
          &.medium { color: #faad14; }
          &.normal { color: #52c41a; }
        }
        
        .separator {
          color: rgba(0, 240, 255, 0.4);
          margin: 0 2px;
        }
      }
      
      .collapse-icon {
        font-size: 18px;
        transition: transform 0.3s ease;
      }
    }
    .title-bg { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent); opacity: 0; transition: opacity 0.3s ease; }
    &:hover .title-bg { opacity: 1; }
    .title-decoration { position: absolute; bottom: -2px; left: 50%; transform: translateX(-50%); width: 60%; height: 2px; background: linear-gradient(90deg, transparent, #00f0ff, transparent); box-shadow: 0 0 10px rgba(0, 240, 255, 0.8); }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 1) 50%, transparent);
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
    animation: scanline 2s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent);
    animation: dataflow 3s ease-in-out infinite;
  }

  .memory-grid {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 240, 255, 0.2);
    cursor: pointer;
    transition: all 0.3s ease;

    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;

      .header-icon {
        font-size: 20px;
      }

      .header-title {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
      }
    }

    .header-right {
      .collapse-icon {
        font-size: 18px;
        color: #00f0ff;
        transition: transform 0.3s ease;
      }
    }
  }

  .memory-grid {
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
    overflow: hidden;
    min-height: 0;

    .columns-container {
      display: flex;
      flex: 1;
      gap: 8px;
      padding: 8px;
      min-height: 0;
      
      .column-left,
      .column-right {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0;
        min-width: 0;
        border-right: 1px solid rgba(0, 240, 255, 0.15);
        padding-right: 8px;
        
        &:last-child {
          border-right: none;
          padding-right: 0;
        }
      }
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 6px;
      border-bottom: 1px solid rgba(0, 240, 255, 0.1);
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(0, 240, 255, 0.08);
      }

      .info-label {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.55);
        width: 60px;
        font-weight: 500;
        letter-spacing: 0.4px;
        flex-shrink: 0;
      }

      .info-value {
        font-size: 12px;
        font-weight: 700;
        color: #fff;
        font-family: 'Consolas', 'Monaco', monospace;
        text-align: right;
        flex: 1;
        text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);
        margin-left: 6px;
        word-break: break-word;
        overflow: hidden;
        text-overflow: ellipsis;
        width:70px;

        span.used { color: #faad14; }
        span.free { color: #52c41a; }

        &.high { color: #ff4d4f; }
        &.medium { color: #faad14; }
        &.normal { color: #52c41a; }

        .unit {
          font-size: 8px;
          color: rgba(0, 240, 255, 0.7);
          margin-left: 2px;
          font-weight: 600;
        }
      }
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 5px 6px;
      background: linear-gradient(90deg, rgba(0, 240, 255, 0.08), transparent);
      border-bottom: 2px solid rgba(0, 240, 255, 0.3);
      font-size: 11px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 6px rgba(0, 240, 255, 0.5);
      letter-spacing: 0.4px;
      margin-bottom: 2px;
      
      i {
        font-size: 12px;
      }
    }
  }

  .memory-card {
    display: none;
  }
  
  .disk-card {
    display: none;
  }
  
  // 无数据状态
  .no-data-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 48px 24px;
    min-height: 200px;
    
    .hologram-container {
      position: relative;
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .hologram-ring {
        position: absolute;
        border: 2px solid rgba(0, 240, 255, 0.4);
        border-radius: 50%;
        
        &.ring-1 {
          width: 120px;
          height: 120px;
          animation: pulse-ring 2s ease-in-out infinite;
        }
        
        &.ring-2 {
          width: 90px;
          height: 90px;
          animation: pulse-ring 2s ease-in-out 0.3s infinite;
        }
        
        &.ring-3 {
          width: 60px;
          height: 60px;
          animation: pulse-ring 2s ease-in-out 0.6s infinite;
        }
      }
      
      .hologram-icon {
        position: relative;
        z-index: 1;
        font-size: 48px;
        color: #00f0ff;
        text-shadow: 0 0 20px rgba(0, 240, 255, 0.8),
                     0 0 40px rgba(0, 240, 255, 0.6),
                     0 0 60px rgba(0, 240, 255, 0.4);
        animation: float 3s ease-in-out infinite;
        
        i {
          display: block;
        }
      }
    }
    
    .no-data-text {
      text-align: center;
      
      .main-text {
        font-size: 16px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
        margin-bottom: 8px;
        letter-spacing: 1px;
      }
      
      .sub-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.5);
        animation: pulse-text 2s ease-in-out infinite;
      }
    }
  }
}

@keyframes pulse-ring {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes scanline {
  0% { transform: translateY(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(500px); opacity: 0; }
}

@keyframes dataflow {
  0% { left: -100%; }
  100% { left: 100%; }
}
</style>
